import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
} from 'antd';
import {
  ApiOutlined,
  ArrowLeftOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ScanOutlined,
  DashboardOutlined,
  InboxOutlined,
  UserOutlined,
} from '@ant-design/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import BreadCrumb from '../modules/breadcrumbs';
import * as partnerApi from '../modules/partners/api';
import PartnerItemsModal from '../components/Modals/PartnerItemsModal';
import { ReactSVG } from 'react-svg';
import RoutesTable from '../components/Tables/RoutesTable';
import OrdersTable from '../components/Tables/OrdersTable';
import OrdersFilter from '../components/Forms/Filters/OrdersFilter';
import { selectAllRoutes } from '../modules/routes/stores/slice';
import { selectAllOrders } from '../modules/orders/stores/slice';
import { doGetRoutes } from '../modules/routes/stores/thunk';
import { doGetOrders, doDeleteOrders } from '../modules/orders/stores/thunk';
import { selectAllPartnerOrders } from '../modules/partner-orders/stores/slice';
import { doGetPartnerOrders } from '../modules/partner-orders/stores/thunk';
import { DATE_BACKEND_FORMAT, ORDER_STATUSES, PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../constants';
import PartnerStats from './PartnerStats';
import './PartnerDetail.scss';

const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  { label: 'Product', value: 'PRODUCT' },
  { label: 'Empty', value: 'EMPTY' },
];

const CATEGORY_COLORS = {
  PRODUCT: 'blue',
  EMPTY: 'orange',
};

const PartnerDetailPage = ({ defaultTab, viewOnly }) => {
  const { t } = useTranslation(['partners', 'common']);
  const { id: paramId } = useParams();
  const authUser = useSelector((state) => state.auth.user);
  const id = viewOnly ? authUser?.partnerId : paramId;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(defaultTab || 'overview');
  useEffect(() => {
    if (defaultTab && defaultTab !== activeTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [partnerForm] = Form.useForm();
  const [itemTypeForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [partner, setPartner] = useState(null);
  const [itemTypes, setItemTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemTypesLoading, setItemTypesLoading] = useState(false);

  // Partner edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Item type modal
  const [itemTypeModalOpen, setItemTypeModalOpen] = useState(false);
  const [editingItemType, setEditingItemType] = useState(null);
  const [itemTypeSaving, setItemTypeSaving] = useState(false);
  const [manageItemsType, setManageItemsType] = useState(null);

  // Route history
  // Route History (reuses global routes Redux store)
  const routes = useSelector(selectAllRoutes);
  const { metadata: routesMetadata, isFetching: routesFetching } = useSelector((state) => state.route);
  const [routesLoaded, setRoutesLoaded] = useState(false);
  const [routeSearchValue, setRouteSearchValue] = useState('');

  // Collected Items
  const [collectedItems, setCollectedItems] = useState([]);
  const [collectedItemsLoading, setCollectedItemsLoading] = useState(false);
  const [collectedItemsLoaded, setCollectedItemsLoaded] = useState(false);
  const [collectedItemsPagination, setCollectedItemsPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
  });

  // Viewers
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersLoaded, setViewersLoaded] = useState(false);
  const [viewerModalOpen, setViewerModalOpen] = useState(false);
  const [editingViewer, setEditingViewer] = useState(null);
  const [viewerSaving, setViewerSaving] = useState(false);
  const [viewerForm] = Form.useForm();

  // Orders (use partner-orders store when viewOnly, regular orders store when admin)
  const adminOrders = useSelector(selectAllOrders);
  const partnerOrders = useSelector(selectAllPartnerOrders);
  const allOrders = viewOnly ? partnerOrders : adminOrders;
  const { metadata: adminOrdersMeta, isFetching: adminOrdersFetching } = useSelector((state) => state.order);
  const { metadata: partnerOrdersMeta, isFetching: partnerOrdersFetching } = useSelector((state) => state.partnerOrder);
  const ordersMetadata = viewOnly ? partnerOrdersMeta : adminOrdersMeta;
  const ordersFetching = viewOnly ? partnerOrdersFetching : adminOrdersFetching;
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [orderSearchValue, setOrderSearchValue] = useState('');
  const [orderFilterQuery, setOrderFilterQuery] = useState({});
  const [isOrderFilterOpen, setIsOrderFilterOpen] = useState(false);
  const orderTableConfigRef = useRef({});

  // Webhook config
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookForm] = Form.useForm();

  const breadCrumbs = viewOnly
    ? [{ text: partner?.name || '...' }]
    : [{ text: 'PARTNERS', url: '/admin/partners' }, { text: partner?.name || '...' }];

  const fetchPartner = useCallback(async () => {
    setLoading(true);
    const { response } = viewOnly ? await partnerApi.getMyPartner() : await partnerApi.getPartner(id);
    if (response) {
      setPartner(response);
    }
    setLoading(false);
  }, [id, viewOnly]);

  const fetchItemTypes = useCallback(async () => {
    setItemTypesLoading(true);
    const { response } = viewOnly ? await partnerApi.getMyPartnerItemTypes() : await partnerApi.getPartnerItemTypes(id);
    if (response) {
      setItemTypes(response);
    }
    setItemTypesLoading(false);
  }, [id, viewOnly]);

  const fetchPartnerRoutes = useCallback(
    (params = {}) => {
      dispatch(doGetRoutes({ partnerId: id, page: 1, take: PAGE_SIZE, ...params }));
      setRoutesLoaded(true);
    },
    [id, dispatch],
  );

  const fetchPartnerOrders = useCallback(
    (params = {}) => {
      if (viewOnly) {
        dispatch(doGetPartnerOrders({ page: 1, take: PAGE_SIZE, ...params }));
      } else {
        dispatch(doGetOrders({ partnerId: id, page: 1, take: PAGE_SIZE, ...params }));
      }
      setOrdersLoaded(true);
    },
    [id, dispatch, viewOnly],
  );

  useEffect(() => {
    fetchPartner();
    fetchItemTypes();
  }, [fetchPartner, fetchItemTypes]);

  // --- Partner Edit ---
  const handleOpenEditModal = () => {
    partnerForm.setFieldsValue({
      name: partner?.name,
      code: partner?.code,
      contactEmail: partner?.contactEmail,
      contactPhone: partner?.contactPhone,
      address: partner?.address,
      notes: partner?.notes,
    });
    setEditModalOpen(true);
  };

  const handleSavePartner = async (values) => {
    setEditSaving(true);
    const { response } = await partnerApi.updatePartner({ id: Number(id), ...values });
    if (response) {
      setPartner(response);
      messageApi.success('Partner updated');
      setEditModalOpen(false);
    } else {
      messageApi.error('Failed to update partner');
    }
    setEditSaving(false);
  };

  // --- Item Type CRUD ---
  const handleOpenItemTypeModal = (itemType = null) => {
    setEditingItemType(itemType);
    if (itemType) {
      itemTypeForm.setFieldsValue({
        name: itemType.name,
        category: itemType.category || 'PRODUCT',
        codePrefix: itemType.codePrefix,
        unitOfMeasure: itemType.unitOfMeasure,
        requiresScan: itemType.requiresScan ?? false,
        requiresPhoto: itemType.requiresPhoto ?? false,
        requiresWeight: itemType.requiresWeight ?? false,
      });
    } else {
      itemTypeForm.resetFields();
      itemTypeForm.setFieldsValue({
        category: 'PRODUCT',
        requiresScan: false,
        requiresPhoto: false,
        requiresWeight: false,
      });
    }
    setItemTypeModalOpen(true);
  };

  const handleSaveItemType = async (values) => {
    setItemTypeSaving(true);
    if (editingItemType) {
      const { response } = await partnerApi.updatePartnerItemType({ id: editingItemType.id, ...values });
      if (response) {
        setItemTypes((prev) => prev.map((t) => (t.id === editingItemType.id ? response : t)));
        messageApi.success('Item type updated');
        setItemTypeModalOpen(false);
      } else {
        messageApi.error('Failed to update item type');
      }
    } else {
      const { response } = await partnerApi.createPartnerItemType(id, values);
      if (response) {
        setItemTypes((prev) => [...prev, response]);
        messageApi.success('Item type created');
        setItemTypeModalOpen(false);
      } else {
        messageApi.error('Failed to create item type');
      }
    }
    setItemTypeSaving(false);
  };

  const handleDeleteItemType = async (itemTypeId) => {
    const { response } = await partnerApi.removePartnerItemType(itemTypeId);
    if (response) {
      setItemTypes((prev) => prev.filter((t) => t.id !== itemTypeId));
      messageApi.success('Item type deleted');
    } else {
      messageApi.error('Failed to delete item type');
    }
  };

  // --- Collected Items ---
  const fetchCollectedItems = useCallback(
    async (page = 1) => {
      setCollectedItemsLoading(true);
      const params = { page, take: 20 };
      const { response } = await partnerApi.getPartnerCollectedItems(id, params);
      if (response) {
        setCollectedItems(response.items || []);
        setCollectedItemsPagination(response.pagination || {});
      }
      setCollectedItemsLoading(false);
      setCollectedItemsLoaded(true);
    },
    [id],
  );

  // --- Viewers ---
  const fetchViewers = useCallback(async () => {
    setViewersLoading(true);
    const { response } = viewOnly
      ? await partnerApi.getMyPartnerViewers()
      : await partnerApi.getPartnerViewers(id, { page: 1, take: 100 });
    if (response?.users) {
      setViewers(response.users);
    }
    setViewersLoading(false);
    setViewersLoaded(true);
  }, [id, viewOnly]);

  const handleOpenViewerModal = (viewer = null) => {
    setEditingViewer(viewer);
    if (viewer) {
      const nameParts = (viewer.name || '').split(' ');
      viewerForm.setFieldsValue({
        giveName: nameParts[0] || '',
        familyName: nameParts.slice(1).join(' ') || '',
        email: viewer.email,
        password: '',
        language: viewer.language || 'fr',
      });
    } else {
      viewerForm.resetFields();
    }
    setViewerModalOpen(true);
  };

  const handleSaveViewer = async (values) => {
    setViewerSaving(true);
    if (editingViewer) {
      const updateData = { ...values };
      if (!updateData.password) delete updateData.password;
      const { response } = await partnerApi.updatePartnerViewer(id, editingViewer.id, updateData);
      if (response) {
        fetchViewers();
        messageApi.success('Viewer updated');
        setViewerModalOpen(false);
      } else {
        messageApi.error('Failed to update viewer');
      }
    } else {
      const { response } = await partnerApi.createPartnerViewer(id, values);
      if (response) {
        fetchViewers();
        messageApi.success('Viewer created');
        setViewerModalOpen(false);
      } else {
        messageApi.error('Failed to create viewer');
      }
    }
    setViewerSaving(false);
  };

  const handleDeleteViewer = async (viewerId) => {
    const { response } = await partnerApi.removePartnerViewer(id, viewerId);
    if (response) {
      setViewers((prev) => prev.filter((v) => v.id !== viewerId));
      messageApi.success('Viewer deleted');
    } else {
      messageApi.error('Failed to delete viewer');
    }
  };

  // --- Webhook Config ---
  const handleOpenWebhookModal = () => {
    webhookForm.setFieldsValue({
      webhookEnabled: partner?.webhookEnabled ?? false,
      webhookUrl: partner?.webhookUrl || '',
      webhookHeaders: partner?.webhookHeaders ? JSON.stringify(partner.webhookHeaders, null, 2) : '',
      webhookTransformer: partner?.webhookTransformer || null,
    });
    setWebhookModalOpen(true);
  };

  const handleSaveWebhook = async (values) => {
    // Parse headers JSON
    let headers = null;
    if (values.webhookHeaders && values.webhookHeaders.trim()) {
      try {
        headers = JSON.parse(values.webhookHeaders);
      } catch {
        messageApi.error('Invalid JSON for headers');
        return;
      }
    }
    setWebhookSaving(true);
    const { response } = await partnerApi.updatePartner({
      id: Number(id),
      webhookEnabled: values.webhookEnabled,
      webhookUrl: values.webhookUrl || null,
      webhookHeaders: headers,
      webhookTransformer: values.webhookTransformer || null,
    });
    if (response) {
      setPartner(response);
      messageApi.success('Webhook configuration updated');
      setWebhookModalOpen(false);
    } else {
      messageApi.error('Failed to update webhook configuration');
    }
    setWebhookSaving(false);
  };

  // --- Tab change handler (lazy load route history / viewers) ---
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (viewOnly) {
      navigate(key === 'overview' ? '/partner' : `/partner/${key}`, { replace: true });
    } else if (key === 'overview') {
      navigate(`/admin/partners/${id}`, { replace: true });
    } else {
      navigate(`/admin/partners/${id}/${key}`, { replace: true });
    }
    if (key === 'routes' && !routesLoaded) {
      fetchPartnerRoutes();
    }
    if (key === 'orders' && !ordersLoaded) {
      fetchPartnerOrders();
    }
    if (key === 'collected' && !collectedItemsLoaded) {
      fetchCollectedItems();
    }
    if (key === 'viewers' && !viewersLoaded) {
      fetchViewers();
    }
  };

  // Auto-load routes/orders when landing on tabs via URL
  useEffect(() => {
    if (defaultTab === 'routes' && !routesLoaded) {
      fetchPartnerRoutes();
    }
  }, [defaultTab, routesLoaded, fetchPartnerRoutes]);

  useEffect(() => {
    if (defaultTab === 'orders' && !ordersLoaded) {
      fetchPartnerOrders();
    }
  }, [defaultTab, ordersLoaded, fetchPartnerOrders]);

  useEffect(() => {
    if (defaultTab === 'collected' && !collectedItemsLoaded) {
      fetchCollectedItems();
    }
  }, [defaultTab, collectedItemsLoaded, fetchCollectedItems]);

  useEffect(() => {
    if (defaultTab === 'viewers' && !viewersLoaded) {
      fetchViewers();
    }
  }, [defaultTab, viewersLoaded, fetchViewers]);

  // --- Table columns ---
  const itemTypeColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => <Tag color={CATEGORY_COLORS[cat] || 'default'}>{cat || '-'}</Tag>,
    },
    {
      title: 'Code Prefix',
      dataIndex: 'codePrefix',
      key: 'codePrefix',
      render: (val) => val || '-',
    },
    {
      title: 'Unit',
      dataIndex: 'unitOfMeasure',
      key: 'unitOfMeasure',
      render: (val) => val || '-',
    },
    {
      title: 'Flags',
      key: 'flags',
      render: (_, record) => (
        <span className="item-type-flags">
          {record.requiresScan && (
            <Tag icon={<ScanOutlined />} color="processing">
              Scan
            </Tag>
          )}
          {record.requiresPhoto && (
            <Tag icon={<CameraOutlined />} color="processing">
              Photo
            </Tag>
          )}
          {record.requiresWeight && (
            <Tag icon={<DashboardOutlined />} color="processing">
              Weight
            </Tag>
          )}
          {!record.requiresScan && !record.requiresPhoto && !record.requiresWeight && '-'}
        </span>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (val) => (
        <Tag color={val !== false ? 'green' : 'default'} icon={val !== false ? <CheckCircleOutlined /> : null}>
          {val !== false ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Items',
      key: 'itemsCount',
      render: (_, record) => (record.category === 'EMPTY' ? record._count?.partnerItems ?? 0 : '-'),
    },
    !viewOnly && {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span className="item-type-actions">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenItemTypeModal(record)}>
            {t('common:edit')}
          </Button>
          {record.category === 'EMPTY' && (
            <Button type="link" size="small" onClick={() => setManageItemsType(record)}>
              {t('items.button')}
            </Button>
          )}
          <Popconfirm
            title={t('deleteItemType')}
            onConfirm={() => handleDeleteItemType(record.id)}
            okText={t('common:yes')}
            cancelText={t('common:no')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {t('common:delete')}
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ].filter(Boolean);

  const handleRouteTableChange = (paginationParams, _filters, sorter) => {
    const params = {
      partnerId: id,
      current: paginationParams.current,
      pageSize: paginationParams.pageSize,
      page: paginationParams.current,
      take: paginationParams.pageSize,
      q: routeSearchValue || undefined,
    };
    if (sorter && sorter.field && sorter.order) {
      params.sortField = sorter.field;
      params.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }
    fetchPartnerRoutes(params);
  };

  const handleRouteSearch = () => {
    fetchPartnerRoutes({ q: routeSearchValue || undefined });
  };

  const routeWithAction = (record) => (
    <div className="actions">
      <Link to={`/admin/route-plans?date=${moment(record.date).format(DATE_BACKEND_FORMAT)}`}>
        <Button type="link" className="edit">
          <ReactSVG className="icon" src="/icons/route-plans.svg" />
        </Button>
      </Link>
    </div>
  );

  // --- Orders tab handlers ---
  const dispatchOrders = useCallback(
    (params) => {
      if (viewOnly) {
        dispatch(doGetPartnerOrders(params));
      } else {
        dispatch(doGetOrders({ partnerId: id, ...params }));
      }
    },
    [viewOnly, id, dispatch],
  );

  const handleOrderTableChange = (params) => {
    const newParams = { ...orderFilterQuery, ...params };
    delete newParams.pageSizeOptions;
    const page = Number(newParams.current || 1);
    const take = Number(newParams.pageSize) || PAGE_SIZE;
    dispatchOrders({ page, take, ...newParams });
  };

  const handleOrderSearch = () => {
    const params = { ...orderFilterQuery, q: orderSearchValue || undefined, current: 1 };
    dispatchOrders({ page: 1, take: orderTableConfigRef.current?.pageSize || PAGE_SIZE, ...params });
  };

  const handleOrderFilter = (params) => {
    const newParams = { ...params };
    if (!viewOnly) newParams.partnerId = id;
    delete newParams.pageSizeOptions;
    setOrderFilterQuery(newParams);
    dispatchOrders({
      page: newParams.current || 1,
      take: Number(newParams.pageSize) || orderTableConfigRef.current?.pageSize || PAGE_SIZE,
      ...newParams,
    });
  };

  const DELETABLE_STATUSES = [
    ORDER_STATUSES.DRAFT,
    ORDER_STATUSES.GEOCODING,
    ORDER_STATUSES.ERROR,
    ORDER_STATUSES.UNASSIGNED,
  ];

  const handleDeleteOrder = async (record) => {
    try {
      await dispatch(doDeleteOrders({ orderCodes: [record.orderCode] })).unwrap();
      messageApi.success(`Order ${record.orderCode} deleted`);
      fetchPartnerOrders({ ...orderFilterQuery });
    } catch (error) {
      messageApi.error(error?.message || 'Failed to delete order');
    }
  };

  const orderWithAction = (record) => {
    if (viewOnly) {
      return (
        <div className="actions">
          <Link to={`/partner/orders/${record.orderCode}`}>
            <Button type="link" className="edit">
              <ReactSVG className="icon" src="/icons/edit.svg" />
            </Button>
          </Link>
        </div>
      );
    }
    const isDeletable = DELETABLE_STATUSES.includes(record.status);
    return (
      <div className="actions">
        <Link to={`/admin/orders/${record.orderCode}`}>
          <Button type="link" className="edit">
            <ReactSVG className="icon" src="/icons/edit.svg" />
          </Button>
        </Link>
        {isDeletable ? (
          <Popconfirm
            title={t('common:delete')}
            description={`Delete order ${record.orderCode}?`}
            onConfirm={() => handleDeleteOrder(record)}
            okText={t('common:delete')}
            cancelText={t('common:cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/more.svg" />
            </Button>
          </Popconfirm>
        ) : (
          <Button type="link" className="more" disabled>
            <ReactSVG className="icon" src="/icons/more.svg" />
          </Button>
        )}
        {![ORDER_STATUSES.DRAFT, ORDER_STATUSES.GEOCODING, ORDER_STATUSES.ERROR].includes(record.status) && (
          <Link to={`/admin/route-plans?date=${moment(record.deliveryDate).format(DATE_BACKEND_FORMAT)}`}>
            <Button type="link" className="more">
              <ReactSVG className="icon" src="/icons/route-plans.svg" />
            </Button>
          </Link>
        )}
      </div>
    );
  };

  const orderActiveFilterCount = [
    orderFilterQuery.name,
    orderFilterQuery.routeId,
    orderFilterQuery.status,
    orderFilterQuery.deliveryDateFrom || orderFilterQuery.deliveryDateTo,
    orderFilterQuery.orderType,
    orderFilterQuery.issueTypes,
  ].filter(Boolean).length;

  const viewerColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (val) => val || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '-'),
    },
    !viewOnly && {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <span>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenViewerModal(record)}>
            {t('common:edit')}
          </Button>
          <Popconfirm
            title={t('deleteViewer')}
            onConfirm={() => handleDeleteViewer(record.id)}
            okText={t('common:yes')}
            cancelText={t('common:no')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {t('common:delete')}
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ].filter(Boolean);

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <>
          {/* Partner Info Card */}
          <Card
            title="Partner Information"
            extra={
              !viewOnly && (
                <Button type="primary" icon={<EditOutlined />} onClick={handleOpenEditModal}>
                  Edit
                </Button>
              )
            }
            className="partner-info-card"
          >
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Name">{partner?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Code">{partner?.code || '-'}</Descriptions.Item>
              <Descriptions.Item label="Email">{partner?.contactEmail || '-'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{partner?.contactPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Address">{partner?.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="Notes" span={2}>
                {partner?.notes || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Item Types Section */}
          <Card
            title="Item Types"
            extra={
              !viewOnly && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenItemTypeModal(null)}>
                  Add Item Type
                </Button>
              )
            }
            className="item-types-card"
          >
            <Table
              dataSource={itemTypes}
              columns={itemTypeColumns}
              rowKey="id"
              loading={itemTypesLoading}
              pagination={false}
              locale={{ emptyText: 'No item types defined yet' }}
              size="small"
            />
          </Card>

          {/* Webhook Configuration */}
          <Card
            title={
              <span>
                <ApiOutlined /> Webhook Configuration
              </span>
            }
            extra={
              !viewOnly && (
                <Button type="primary" icon={<EditOutlined />} onClick={handleOpenWebhookModal}>
                  Configure
                </Button>
              )
            }
            className="webhook-config-card"
          >
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
              <Descriptions.Item label="Status">
                <Tag color={partner?.webhookEnabled ? 'green' : 'default'}>
                  {partner?.webhookEnabled ? 'Active' : 'Inactive'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="URL">{partner?.webhookUrl || '-'}</Descriptions.Item>
              <Descriptions.Item label="Transformer">
                {partner?.webhookTransformer ? (
                  <Tag color="blue">{partner.webhookTransformer.toUpperCase()}</Tag>
                ) : (
                  'Standard'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Custom Headers">
                {partner?.webhookHeaders ? (
                  <pre className="webhook-headers-preview">{JSON.stringify(partner.webhookHeaders, null, 2)}</pre>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Arriving Soon">
                <Switch
                  checked={partner?.arrivingSoonEnabled}
                  disabled={viewOnly}
                  size="small"
                  onChange={async (checked) => {
                    const { response } = await partnerApi.updatePartner({
                      id: Number(id),
                      arrivingSoonEnabled: checked,
                    });
                    if (response) {
                      setPartner(response);
                      messageApi.success(checked ? 'Arriving Soon enabled' : 'Arriving Soon disabled');
                    }
                  }}
                />
                <span style={{ marginLeft: 8, fontSize: 12, color: '#9e9e9e' }}>
                  Notify next delivery as "Arriving soon" in Airtable
                </span>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      ),
    },
    {
      key: 'stats',
      label: 'Stats',
      children: <PartnerStats partnerId={id} viewOnly={viewOnly} />,
    },
    {
      key: 'routes',
      label: 'Routes',
      children: (
        <div className="route-history-section">
          <div className="page-header-table" style={{ marginBottom: 16 }}>
            <div className="search">
              <Input
                onChange={(e) => setRouteSearchValue(e.target.value)}
                onBlur={handleRouteSearch}
                onKeyDown={(e) => e.key === 'Enter' && handleRouteSearch()}
                placeholder="Search routes"
                suffix={<img src="/icons/search.svg" alt="" />}
                value={routeSearchValue}
              />
            </div>
          </div>
          <RoutesTable
            dataSource={routes}
            rowKey="id"
            isFetching={routesFetching}
            withAction={viewOnly ? undefined : routeWithAction}
            partnerId={id}
            pagination={{
              current: Number(routesMetadata.currentPage) || 1,
              pageSize: PAGE_SIZE,
              total: routesMetadata.totalItems || 0,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              showSizeChanger: true,
              showTotal: (total) => `${total} Routes`,
            }}
            onChange={handleRouteTableChange}
          />
        </div>
      ),
    },
    {
      key: 'orders',
      label: 'Orders',
      children: (
        <div className="partner-orders-section">
          <div className="page-header-table" style={{ marginBottom: 16 }}>
            <div className="search">
              <Input
                onChange={(e) => setOrderSearchValue(e.target.value)}
                onBlur={handleOrderSearch}
                onKeyDown={(e) => e.key === 'Enter' && handleOrderSearch()}
                placeholder="Search orders"
                suffix={<img src="/icons/search.svg" alt="" />}
                value={orderSearchValue}
              />
            </div>
            <div className="buttons">
              <Badge count={orderActiveFilterCount} size="small" offset={[-5, 0]}>
                <Button className="FilterButton ButtonFilter" onClick={() => setIsOrderFilterOpen(true)}>
                  Filter
                  <ReactSVG className="icon" src="/icons/filter_list.svg" />
                </Button>
              </Badge>
              <Button
                type="text"
                onClick={() => orderTableConfigRef.current?.openDrawer?.()}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ReactSVG className="icon" src="/icons/settings.svg" />
              </Button>
            </div>
          </div>
          <OrdersFilter
            open={isOrderFilterOpen}
            onClose={() => setIsOrderFilterOpen(false)}
            query={orderFilterQuery}
            onApply={handleOrderFilter}
            hidePartner
          />
          <div className="ContentMain">
            <OrdersTable
              dataSource={allOrders}
              pagination={{
                current: Number(ordersMetadata.currentPage) || 1,
                pageSize: orderTableConfigRef.current?.pageSize || PAGE_SIZE,
                total: ordersMetadata.totalItems || 0,
                showSizeChanger: false,
                showTotal: (total) => `${total} Orders`,
              }}
              withAction={orderWithAction}
              rowKey="orderCode"
              isFetching={ordersFetching}
              onSelectChange={() => {}}
              onChange={handleOrderTableChange}
              configRef={orderTableConfigRef}
              linkPrefix={viewOnly ? '/partner/orders' : '/admin/orders'}
              onPageSizeChange={(size) => {
                dispatchOrders({ page: 1, take: size, ...orderFilterQuery });
              }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'collected',
      label: (
        <span>
          <InboxOutlined /> Collected Empties
        </span>
      ),
      children: (
        <div className="collected-empties-section">
          <div
            className="page-header-table"
            style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}
          >
            <Input.Search placeholder="Search collected items..." allowClear style={{ width: 300 }} disabled />
          </div>
          <Table
            dataSource={collectedItems}
            rowKey="id"
            loading={collectedItemsLoading}
            size="small"
            locale={{ emptyText: 'No collected items' }}
            pagination={{
              current: collectedItemsPagination.currentPage || 1,
              pageSize: 20,
              total: collectedItemsPagination.totalItems || 0,
              showTotal: (total) => `${total} items`,
            }}
            onChange={(pag) => fetchCollectedItems(pag.current)}
            columns={[
              {
                title: 'Date',
                dataIndex: 'collectedAt',
                key: 'collectedAt',
                width: 140,
                render: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '-'),
              },
              {
                title: 'Route',
                dataIndex: ['route', 'id'],
                key: 'routeId',
                width: 80,
                render: (val) => (val ? <Link to={`/admin/orders?routeId=${val}`}>{val}</Link> : '-'),
              },
              {
                title: 'Order Ref',
                key: 'orderRef',
                width: 140,
                render: (_, record) => record.order?.partnerOrderRef || record.order?.orderCode || '-',
              },
              {
                title: 'Item Type',
                key: 'itemType',
                width: 160,
                render: (_, record) => {
                  const name = record.partnerItemType?.name || record.description || '-';
                  const cat = record.partnerItemType?.category;
                  return (
                    <span>
                      {name}
                      {cat && (
                        <Tag color={cat === 'EMPTY' ? 'orange' : 'blue'} style={{ marginLeft: 6, fontSize: 10 }}>
                          {cat}
                        </Tag>
                      )}
                    </span>
                  );
                },
              },
              {
                title: 'Qty',
                dataIndex: 'quantity',
                key: 'quantity',
                width: 60,
                align: 'center',
              },
              {
                title: 'Weight',
                key: 'weight',
                width: 100,
                render: (_, record) => {
                  if (!record.weight) return '-';
                  const unit = record.partnerItemType?.unitOfMeasure || '';
                  return `${record.weight} ${unit}`.trim();
                },
              },
              {
                title: 'Barcode',
                dataIndex: 'scannedCode',
                key: 'scannedCode',
                width: 140,
                render: (val) => val || '-',
              },
              {
                title: 'Driver',
                key: 'driver',
                width: 120,
                render: (_, record) => record.collectedByUser?.name || '-',
              },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'viewers',
      label: (
        <span>
          <UserOutlined /> Viewers
        </span>
      ),
      children: (
        <Card
          title="Partner Viewers"
          extra={
            !viewOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenViewerModal(null)}>
                Add Viewer
              </Button>
            )
          }
        >
          <Table
            dataSource={viewers}
            columns={viewerColumns}
            rowKey="id"
            loading={viewersLoading}
            locale={{ emptyText: 'No viewers created yet' }}
            size="small"
            pagination={false}
          />
        </Card>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <div className="PartnerDetailPage Content">
        <div className="ContentHeader">
          <BreadCrumb breadCrumbs={breadCrumbs} />
        </div>

        <div className="partner-detail-content">
          {!viewOnly && (
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/admin/partners')}
              className="back-button"
            >
              Back to Partners
            </Button>
          )}

          <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} />
        </div>

        {!viewOnly && (
          <>
            {/* Edit Partner Modal */}
            <Modal
              title="Edit Partner"
              open={editModalOpen}
              onCancel={() => setEditModalOpen(false)}
              footer={null}
              destroyOnClose
              width={600}
            >
              <Form form={partnerForm} layout="vertical" onFinish={handleSavePartner} requiredMark={false}>
                <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Partner name is required' }]}>
                  <Input placeholder="Partner name" />
                </Form.Item>
                <Form.Item label="Code" name="code">
                  <Input disabled />
                </Form.Item>
                <Form.Item
                  label="Contact Email"
                  name="contactEmail"
                  rules={[{ type: 'email', message: 'Invalid email' }]}
                >
                  <Input placeholder="ops@partner.com" />
                </Form.Item>
                <Form.Item label="Contact Phone" name="contactPhone">
                  <Input placeholder="+41..." />
                </Form.Item>
                <Form.Item label="Address" name="address">
                  <Input placeholder="Partner address" />
                </Form.Item>
                <Form.Item label="Notes" name="notes">
                  <TextArea rows={3} placeholder="Internal notes" />
                </Form.Item>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={editSaving}>
                      Save
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>

            {/* Item Type Modal */}
            <Modal
              title={editingItemType ? 'Edit Item Type' : 'Add Item Type'}
              open={itemTypeModalOpen}
              onCancel={() => setItemTypeModalOpen(false)}
              footer={null}
              destroyOnClose
              width={520}
            >
              <Form form={itemTypeForm} layout="vertical" onFinish={handleSaveItemType} requiredMark={false}>
                <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Item type name is required' }]}>
                  <Input placeholder="e.g. Blue crate, Bottle" />
                </Form.Item>
                <Form.Item label="Category" name="category">
                  <Select options={CATEGORY_OPTIONS} placeholder="Select category" />
                </Form.Item>
                <Form.Item label="Code Prefix" name="codePrefix">
                  <Input placeholder="e.g. BC-" />
                </Form.Item>
                <Form.Item label="Unit of Measure" name="unitOfMeasure">
                  <Input placeholder="e.g. pieces, kg" />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="requiresScan" valuePropName="checked">
                      <Checkbox>Requires Scan</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="requiresPhoto" valuePropName="checked">
                      <Checkbox>Requires Photo</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="requiresWeight" valuePropName="checked">
                      <Checkbox>Requires Weight</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setItemTypeModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={itemTypeSaving}>
                      {editingItemType ? 'Update' : 'Create'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>

            {/* Partner Items Modal */}
            <PartnerItemsModal
              open={!!manageItemsType}
              onClose={() => {
                setManageItemsType(null);
                fetchItemTypes();
              }}
              itemType={manageItemsType}
            />

            {/* Webhook Config Modal */}
            <Modal
              title="Webhook Configuration"
              open={webhookModalOpen}
              onCancel={() => setWebhookModalOpen(false)}
              footer={null}
              destroyOnClose
              width={560}
            >
              <Form form={webhookForm} layout="vertical" onFinish={handleSaveWebhook} requiredMark={false}>
                <Form.Item label="Enabled" name="webhookEnabled" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
                <Form.Item label="Payload Transformer" name="webhookTransformer">
                  <Select
                    allowClear
                    placeholder="Standard (default)"
                    options={[
                      { label: 'Standard', value: null },
                      { label: 'Legacy Webhook', value: 'legacy_webhook' },
                      { label: 'ET - Espace Terroir', value: 'et' },
                      { label: 'Bean - Odoo', value: 'bean' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label="Webhook URL"
                  name="webhookUrl"
                  rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
                >
                  <Input placeholder="https://api.partner.com/webhook" />
                </Form.Item>
                <Form.Item
                  label="Custom Headers (JSON)"
                  name="webhookHeaders"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.trim()) return Promise.resolve();
                        try {
                          JSON.parse(value);
                          return Promise.resolve();
                        } catch {
                          return Promise.reject('Invalid JSON');
                        }
                      },
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder={'{\n  "Authorization": "Bearer your-token"\n}'}
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                  />
                </Form.Item>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setWebhookModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={webhookSaving}>
                      Save
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>

            {/* Viewer Modal */}
            <Modal
              title={editingViewer ? 'Edit Viewer' : 'Add Viewer'}
              open={viewerModalOpen}
              onCancel={() => setViewerModalOpen(false)}
              footer={null}
              destroyOnClose
              width={520}
            >
              <Form form={viewerForm} layout="vertical" onFinish={handleSaveViewer} requiredMark={false}>
                <Form.Item
                  label="First Name"
                  name="giveName"
                  rules={[{ required: !editingViewer, message: 'First name is required' }]}
                >
                  <Input placeholder="First name" />
                </Form.Item>
                <Form.Item label="Last Name" name="familyName">
                  <Input placeholder="Last name" />
                </Form.Item>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: 'Email is required' },
                    { type: 'email', message: 'Invalid email' },
                  ]}
                >
                  <Input placeholder="viewer@partner.com" />
                </Form.Item>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={
                    editingViewer
                      ? []
                      : [
                          { required: true, message: 'Password is required' },
                          { min: 6, message: 'Minimum 6 characters' },
                        ]
                  }
                >
                  <Input.Password placeholder={editingViewer ? 'Leave empty to keep current' : 'Password'} />
                </Form.Item>
                <Form.Item label={t('viewerLanguage')} name="language" initialValue="fr">
                  <Select>
                    <Select.Option value="fr">{t('viewerLanguageFr')}</Select.Option>
                    <Select.Option value="en">{t('viewerLanguageEn')}</Select.Option>
                  </Select>
                </Form.Item>
                <Row justify="end" gutter={8}>
                  <Col>
                    <Button onClick={() => setViewerModalOpen(false)}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button type="primary" htmlType="submit" loading={viewerSaving}>
                      {editingViewer ? 'Update' : 'Create'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal>
          </>
        )}
      </div>
    </Spin>
  );
};

export default PartnerDetailPage;
